/**
 * Properties Behavior Editor
 * UI for editing property declarations (name + type, no values) and states
 * (which hold the actual property values). The 'default' state holds base values.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Properties, Renderer, isPropertyBinding, type PropertyValue, type PropertyDeclaration, type StateDefinition } from 'trace-viewer/behaviors';
import {
    bindRendererProperty,
    defaultStateTransition,
    addStateForSubtree,
    deleteStateForSubtree,
    renameStateForSubtree,
    resolveStateOwner,
} from 'trace-viewer/editor';
import { applyPreview } from './state-preview';
import { isObjectLocal, stateHome, stripSessionFor } from './state-routing';
import { useDragReorder, reorder, dropIndicatorStyle } from '../components/use-drag-reorder';
import { sharedStyles as styles, ValueEditor, StateNameInput, StateDetailEditor, type PropertyType } from './state-ui';
import { UNIT_OPTIONS, applyUnits, declarationUnits, supportsUnits, unitSuffix, type PropertyUnits } from './property-units';

export type { PropertyType } from './state-ui';

const PROPERTY_TYPES: PropertyType[] = ['string', 'number', 'boolean', 'color', 'vec3'];

// ============================================================================
// Declarations Editor Component
// ============================================================================

/** Get default value for a property type */
function defaultValueForType(type: PropertyType): PropertyValue {
    switch (type) {
        case 'string': return '';
        case 'number': return 0;
        case 'boolean': return false;
        case 'color': return '#ffffff';
        case 'vec3': return [0, 0, 0];
        case 'vec4': return [0, 0, 0, 0];
    }
}

/** Deferred-rename input for declaration names — renames state keys only on blur/Enter */
const DeclNameInput: React.FC<{
    name: string;
    isDuplicate: boolean;
    onChange: (name: string) => void;
    onRename: (oldName: string, newName: string) => void;
}> = ({ name, isDuplicate, onChange, onRename }) => {
    const [value, setValue] = useState(name);
    const originalRef = useRef(name);

    // Sync when parent changes the name externally
    useEffect(() => { setValue(name); }, [name]);

    const handleSubmit = () => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== originalRef.current) {
            onRename(originalRef.current, trimmed);
            originalRef.current = trimmed;
        }
    };

    return (
        <input
            type="text"
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
                onChange(e.target.value);
            }}
            onBlur={handleSubmit}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            placeholder="name"
            style={{
                ...styles.input,
                width: '80px',
                flex: 1,
                fontSize: '11px',
                color: isDuplicate ? '#e8a838' : '#fff',
            }}
        />
    );
};

type RendererBinding = 'materialColor' | 'materialSrc' | 'opacity';

interface DeclarationsEditorProps {
    declarations: PropertyDeclaration[];
    sheetDeclarations?: PropertyDeclaration[];
    /** Ancestor-declared property names (for inheritance indicator) */
    ancestorProperties?: Map<string, PropertyDeclaration>;
    states: Record<string, StateDefinition>;
    /** Atomic update — declarations and states change together */
    onChange: (decls: PropertyDeclaration[], states: Record<string, StateDefinition>) => void;
    onBindToRenderer?: (propertyName: string, binding: RendererBinding) => void;
    /** Current material src of slot 0, used to seed a new material binding so the visual doesn't change. Called on click, not render. */
    getMaterialSrcSeed?: () => string | undefined;
}

const DeclarationsEditor: React.FC<DeclarationsEditorProps> = ({
    declarations, sheetDeclarations, ancestorProperties, states, onChange, onBindToRenderer, getMaterialSrcSeed,
}) => {
    const handleReorder = useCallback((from: number, slot: number) => {
        onChange(reorder(declarations, from, slot), states);
    }, [declarations, states, onChange]);

    const { dragIndex, dropSlot, getContainerProps, getDraggableProps } = useDragReorder(declarations.length, handleReorder);

    // Add-declaration popover
    const [showAddPopover, setShowAddPopover] = useState(false);
    const [addPopoverRect, setAddPopoverRect] = useState({ top: 0, left: 0, width: 0 });
    const addButtonRef = useRef<HTMLButtonElement>(null);
    const addPopoverRef = useRef<HTMLDivElement>(null);

    const computeAddPopoverRect = () => {
        if (!addButtonRef.current) return;
        const rect = addButtonRef.current.getBoundingClientRect();
        const inset = rect.width * 0.05;
        setAddPopoverRect({ top: rect.top - 4, left: rect.left + inset, width: rect.width - inset * 2 });
    };

    const toggleAddPopover = () => {
        if (!showAddPopover) {
            computeAddPopoverRect();
            setShowAddPopover(true);
        } else {
            setShowAddPopover(false);
        }
    };

    // Click outside to close add popover
    useEffect(() => {
        if (!showAddPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (
                addPopoverRef.current && !addPopoverRef.current.contains(e.target as Node) &&
                addButtonRef.current && !addButtonRef.current.contains(e.target as Node)
            ) {
                setShowAddPopover(false);
            }
        };
        const id = requestAnimationFrame(() => document.addEventListener('mousedown', handleClick));
        return () => { cancelAnimationFrame(id); document.removeEventListener('mousedown', handleClick); };
    }, [showAddPopover]);

    /** Generate a unique property name starting from the given base */
    const uniqueName = (base: string): string => {
        if (!declarations.some(d => d.name === base)) return base;
        let i = 2;
        while (declarations.some(d => d.name === `${base}${i}`)) i++;
        return `${base}${i}`;
    };

    const handleAdd = (type: PropertyType, binding?: RendererBinding) => {
        let name: string;
        if (binding === 'materialColor') {
            name = uniqueName('color');
        } else if (binding === 'materialSrc') {
            name = uniqueName('material');
        } else if (binding === 'opacity') {
            name = uniqueName('opacity');
        } else {
            name = uniqueName('prop');
        }

        const defaultVal = binding === 'opacity' ? 1 : defaultValueForType(type);
        const decl: PropertyDeclaration = binding === 'opacity'
            ? { name, type: 'number', value: defaultVal, min: 0, max: 1, step: 0.05 } as PropertyDeclaration
            : binding === 'materialSrc'
                // Seed with the current material so binding is visually a no-op;
                // '' means "native material". kind drives asset-picker UI + publish validation.
                ? { name, type: 'string', kind: 'material', value: getMaterialSrcSeed?.() ?? '' }
                : { name, type, value: defaultVal } as PropertyDeclaration;

        onChange([...declarations, decl], states);

        if (binding && onBindToRenderer) {
            onBindToRenderer(name, binding);
        }
        setShowAddPopover(false);
    };

    const handleChange = (index: number, updated: PropertyDeclaration) => {
        const prev = declarations[index];
        const newDecls = [...declarations];
        newDecls[index] = updated;

        let newStates = states;

        // If type changed, reset value and state override values
        if (prev.type !== updated.type) {
            const newDefault = defaultValueForType(updated.type);
            newDecls[index] = { ...updated, value: newDefault } as PropertyDeclaration;
            // Also reset any state overrides for this property
            if (newStates) {
                newStates = { ...newStates };
                for (const [stateName, stateDef] of Object.entries(newStates)) {
                    const propName = updated.name || prev.name;
                    if (propName in stateDef.values) {
                        newStates[stateName] = { ...stateDef, values: { ...stateDef.values, [propName]: newDefault } };
                    }
                }
            }
        }

        onChange(newDecls, newStates);
    };

    /** Rename state value keys when a declaration name is finalized (blur/Enter) */
    const handleRename = (index: number, oldName: string, newName: string) => {
        if (!oldName || !newName || oldName === newName) return;
        const newStates = { ...states };
        for (const [stateName, stateDef] of Object.entries(newStates)) {
            if (oldName in stateDef.values) {
                const { [oldName]: val, ...rest } = stateDef.values;
                newStates[stateName] = { ...stateDef, values: { ...rest, [newName]: val } };
            }
        }
        onChange(declarations, newStates);
    };

    const handleRemove = (index: number) => {
        const removed = declarations[index];
        const newDecls = declarations.filter((_, i) => i !== index);
        const newStates = { ...states };
        for (const [stateName, stateDef] of Object.entries(newStates)) {
            if (removed.name in stateDef.values) {
                const { [removed.name]: _, ...rest } = stateDef.values;
                newStates[stateName] = { ...stateDef, values: rest };
            }
        }
        onChange(newDecls, newStates);
    };

    // Sheet declarations that aren't overridden by inline
    const inlineNames = new Set(declarations.map(d => d.name));
    const visibleSheetDecls = (sheetDeclarations ?? []).filter(d => !inlineNames.has(d.name));

    return (
        <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                Declarations
            </div>
            {/* Read-only sheet declarations */}
            {visibleSheetDecls.map((decl) => {
                const sheetSuffix = unitSuffix(declarationUnits(decl));
                return (
                <div key={`sheet-${decl.name}`} style={{
                    display: 'flex', gap: '4px', alignItems: 'center', padding: '4px 6px',
                    backgroundColor: '#1a1a1a', borderRadius: '3px', marginBottom: '3px',
                    opacity: 0.6, borderLeft: '2px solid #446',
                }}>
                    <span style={{ fontSize: '11px', color: '#99a', width: '80px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{decl.name}</span>
                    <span style={{ fontSize: '11px', color: '#778', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {typeof decl.value === 'object' ? JSON.stringify(decl.value) : String(decl.value)}
                        {sheetSuffix ? ` ${sheetSuffix}` : ''}
                    </span>
                    <span style={{ fontSize: '10px', color: '#556' }}>{decl.type}</span>
                    <span style={{ fontSize: '9px', color: '#446' }}>sheet</span>
                </div>
                );
            })}
            {visibleSheetDecls.length > 0 && declarations.length > 0 && (
                <div style={{ borderBottom: '1px solid #333', margin: '4px 0' }} />
            )}
            {declarations.length === 0 && visibleSheetDecls.length === 0 ? (
                <div style={{ color: '#555', fontSize: '11px', fontStyle: 'italic', marginBottom: '6px' }}>
                    No properties declared
                </div>
            ) : (
                declarations.map((decl, index) => {
                    const isDuplicate = decl.name !== '' && declarations.findIndex(d => d.name === decl.name) < index;
                    const isDragging = dragIndex === index;
                    const isPinned = decl.inherits === false;
                    const ancestorDecl = ancestorProperties?.get(decl.name);
                    const hasAncestor = !!(ancestorDecl && decl.name);
                    const showTopLine = dropSlot === index;
                    const showBottomLine = index === declarations.length - 1 && dropSlot === index + 1;
                    const isNumber = decl.type === 'number';
                    // Length units re-label `value`/`min`/`max`/`step`; they never rescale them.
                    const units = declarationUnits(decl);
                    const suffix = unitSuffix(units);
                    const unitLabel = suffix ? ` (${suffix})` : '';
                    return (
                        <div
                            key={index}
                            style={{ position: 'relative', marginBottom: '3px' }}
                            {...getContainerProps(index)}
                        >
                            {showTopLine && (
                                <div style={{ ...dropIndicatorStyle, top: -1 }} />
                            )}
                            {showBottomLine && (
                                <div style={{ ...dropIndicatorStyle, bottom: -1 }} />
                            )}
                            <div
                                {...getDraggableProps(index)}
                                style={{
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center',
                                    padding: '4px 6px',
                                    backgroundColor: '#1e1e1e',
                                    borderRadius: '3px',
                                    opacity: isDragging ? 0.4 : 1,
                                    cursor: 'grab',
                                    borderLeft: isDuplicate ? '2px solid #e8a838' : '2px solid transparent',
                                }}
                                title={isDuplicate ? `"${decl.name}" is already declared above -- this duplicate is ignored` : undefined}
                            >
                                <DeclNameInput
                                    name={decl.name}
                                    isDuplicate={isDuplicate}
                                    onChange={(newName) => handleChange(index, { ...decl, name: newName })}
                                    onRename={(oldName, newName) => handleRename(index, oldName, newName)}
                                />
                                <ValueEditor
                                    type={decl.type}
                                    value={decl.value}
                                    onChange={(val) => {
                                        handleChange(index, { ...decl, value: val } as PropertyDeclaration);
                                    }}
                                    flex
                                    min={(decl as PropertyDeclaration & { min?: number }).min}
                                    max={(decl as PropertyDeclaration & { max?: number }).max}
                                    step={(decl as PropertyDeclaration & { step?: number }).step}
                                    kind={(decl as PropertyDeclaration & { kind?: string }).kind}
                                    suffix={suffix}
                                />
                                <select
                                    value={decl.type}
                                    onChange={(e) => {
                                        const newType = e.target.value as PropertyType;
                                        // Strip type-specific fields when changing type
                                        const base: PropertyDeclaration = { name: decl.name, type: newType } as PropertyDeclaration;
                                        if (decl.label) (base as PropertyDeclaration & { label: string }).label = decl.label;
                                        handleChange(index, base);
                                    }}
                                    style={{ ...styles.input, width: '70px', cursor: 'pointer', flexShrink: 0, fontSize: '11px' }}
                                >
                                    {PROPERTY_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                                {supportsUnits(decl.type) && (
                                    <select
                                        value={units ?? ''}
                                        onChange={(e) => {
                                            const next = e.target.value === '' ? undefined : (e.target.value as PropertyUnits);
                                            handleChange(index, applyUnits(decl, next));
                                        }}
                                        style={{ ...styles.input, width: '42px', cursor: 'pointer', flexShrink: 0, fontSize: '10px', padding: '2px 2px' }}
                                        title={`Length unit of value${isNumber ? ', min, max and step' : ''} and of this property's state values. Changing it re-labels the numbers — it does not convert them.`}
                                    >
                                        {UNIT_OPTIONS.map((u) => (
                                            <option key={u.value} value={u.value} title={u.title}>{u.label}</option>
                                        ))}
                                    </select>
                                )}
                                {isNumber && (
                                    <>
                                        <input
                                            type="number"
                                            value={(decl as PropertyDeclaration & { min?: number }).min ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                                handleChange(index, { ...decl, min: val });
                                            }}
                                            placeholder={suffix ? `min ${suffix}` : 'min'}
                                            style={{ ...styles.input, width: '40px', fontSize: '10px', padding: '2px 4px' }}
                                            title={`Minimum value${unitLabel}`}
                                        />
                                        <input
                                            type="number"
                                            value={(decl as PropertyDeclaration & { max?: number }).max ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                                handleChange(index, { ...decl, max: val });
                                            }}
                                            placeholder={suffix ? `max ${suffix}` : 'max'}
                                            style={{ ...styles.input, width: '40px', fontSize: '10px', padding: '2px 4px' }}
                                            title={`Maximum value${unitLabel}`}
                                        />
                                        <input
                                            type="number"
                                            value={(decl as PropertyDeclaration & { step?: number }).step ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                                handleChange(index, { ...decl, step: val });
                                            }}
                                            placeholder={suffix ? `step ${suffix}` : 'step'}
                                            style={{ ...styles.input, width: '40px', fontSize: '10px', padding: '2px 4px' }}
                                            title={`Step increment${unitLabel}`}
                                        />
                                    </>
                                )}
                                <button
                                    onClick={() => {
                                        if (isPinned) {
                                            // Unpin: set inherits to true (ancestor values can override)
                                            handleChange(index, { ...decl, inherits: true });
                                        } else {
                                            // Pin: set inherits to false (local value wins)
                                            handleChange(index, { ...decl, inherits: false });
                                        }
                                    }}
                                    style={{
                                        padding: '0 3px', backgroundColor: 'transparent', border: 'none',
                                        color: isPinned ? '#c9a84c' : '#555', cursor: 'pointer',
                                        fontSize: '11px', flexShrink: 0,
                                    }}
                                    title={isPinned
                                        ? 'Pinned — value inherits to descendants and cannot be overridden by ancestors. Click to unpin.'
                                        : 'Unpinned — value can be overridden by ancestors. Click to pin.'}
                                >
                                    {isPinned ? '\u{1F4CC}' : '\u25CB'}
                                </button>
                                <button
                                    onClick={() => handleRemove(index)}
                                    style={{ padding: '0 3px', backgroundColor: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}
                                    title="Remove declaration"
                                >
                                    x
                                </button>
                            </div>
                            {hasAncestor && (
                                <div style={{ fontSize: '10px', color: '#888', padding: '2px 8px 0', opacity: 0.7 }}>
                                    {isPinned ? 'overriding ancestor' : 'inherited from ancestor'}
                                </div>
                            )}
                            {isDuplicate && (
                                <div style={{ fontSize: '10px', color: '#e8a838', padding: '2px 8px 0', opacity: 0.8 }}>
                                    duplicate name
                                </div>
                            )}
                        </div>
                    );
                })
            )}
            <div style={{ position: 'relative' }}>
                <button
                    ref={addButtonRef}
                    onClick={toggleAddPopover}
                    style={{
                        width: '100%', padding: '5px 8px', backgroundColor: '#333',
                        border: '1px dashed #555', borderRadius: '3px', color: '#888',
                        cursor: 'pointer', fontSize: '11px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}
                >
                    <span style={{ fontSize: '12px' }}>+</span>
                    Declaration
                </button>

                {showAddPopover && (
                    <div
                        ref={addPopoverRef}
                        style={{
                            position: 'fixed',
                            bottom: window.innerHeight - addPopoverRect.top,
                            left: addPopoverRect.left,
                            width: addPopoverRect.width,
                            backgroundColor: '#2d2d2d',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            zIndex: 1000,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                        }}
                    >
                        {/* Property types */}
                        {PROPERTY_TYPES.map(type => (
                            <button
                                key={type}
                                onClick={() => handleAdd(type)}
                                style={{
                                    width: '100%', padding: '6px 12px',
                                    backgroundColor: 'transparent', border: 'none',
                                    color: '#ccc', cursor: 'pointer', fontSize: '12px', textAlign: 'left',
                                }}
                                onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#383838'; }}
                                onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
                            >
                                {type}
                            </button>
                        ))}

                        {/* Binding shortcuts */}
                        {onBindToRenderer && (
                            <>
                                <div style={{ borderTop: '1px solid #3a3a3a', margin: '2px 0' }} />
                                <div style={{ padding: '4px 12px 2px', fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Renderer bindings
                                </div>
                                <button
                                    onClick={() => handleAdd('color', 'materialColor')}
                                    style={{
                                        width: '100%', padding: '6px 12px',
                                        backgroundColor: 'transparent', border: 'none',
                                        color: '#8ab', cursor: 'pointer', fontSize: '12px', textAlign: 'left',
                                    }}
                                    onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#383838'; }}
                                    onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
                                >
                                    Material Color
                                </button>
                                <button
                                    onClick={() => handleAdd('string', 'materialSrc')}
                                    style={{
                                        width: '100%', padding: '6px 12px',
                                        backgroundColor: 'transparent', border: 'none',
                                        color: '#8ab', cursor: 'pointer', fontSize: '12px', textAlign: 'left',
                                    }}
                                    onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#383838'; }}
                                    onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
                                >
                                    Material
                                </button>
                                <button
                                    onClick={() => handleAdd('number', 'opacity')}
                                    style={{
                                        width: '100%', padding: '6px 12px',
                                        backgroundColor: 'transparent', border: 'none',
                                        color: '#8ab', cursor: 'pointer', fontSize: '12px', textAlign: 'left',
                                    }}
                                    onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#383838'; }}
                                    onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
                                >
                                    Opacity
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// States Editor
// ============================================================================

/** Pseudo-state names that auto-trigger from pointer/selection events */
const PSEUDO_STATES = [':hover', ':active', ':selected'] as const;

interface StatesEditorProps {
    behavior: Properties;
    states: Record<string, StateDefinition>;
    propertyNames: string[];
    propertyDefs: Map<string, PropertyDeclaration>;
    onChange: (states: Record<string, StateDefinition>) => void;
    initialState?: string;
    onInitialStateChange?: (name: string | undefined) => void;
}

const StatesEditor: React.FC<StatesEditorProps> = ({ behavior, states, propertyNames, propertyDefs, onChange, initialState, onInitialStateChange }) => {
    const [expandedState, setExpandedState] = useState<string | null>(null);
    const [previewState, setPreviewState] = useState<string | null>(null);

    const addState = () => {
        // Route through the SHARED add action rather than committing a state onto
        // this object's own Properties. States live on the group OWNER (the rig,
        // or an ancestor group this object is a member of) — a panel-local commit
        // would create an owner-lacking orphan state the strip can never list or
        // activate. addStateForSubtree resolves the owner, wraps on first state,
        // applies the no-owner-pose-keying portability rule, keys members, and
        // pushes one bundled undo entry; the resulting commits re-enter the panel
        // via BEHAVIOR_CHANGED, refreshing this editor's data. When the owner is
        // the strip's context, the add goes through the strip's session, so the
        // strip shows the new state (see stripSessionFor).
        const session = stripSessionFor(resolveStateOwner(behavior.sceneObject));
        const added = session
            ? session.add().then((r) => (r === true ? session.active() : null))
            : addStateForSubtree(behavior.sceneObject).then((result) => result?.name ?? null);
        void added.then((name) => {
            if (name !== null && behaviorRef.current === behavior) setExpandedState(name);
        });
    };

    const addPseudoState = (pseudo: string) => {
        if (states[pseudo]) return;
        // Pre-populate with values from local declarations only
        const localDecls = behavior.getData().properties ?? [];
        const values: Record<string, PropertyValue> = {};
        for (const decl of localDecls) {
            values[decl.name] = decl.value;
        }
        onChange({ ...states, [pseudo]: { values, transition: defaultStateTransition() } });
        setExpandedState(pseudo);
    };

    // The ref (not React state) holds the preview, so the sync runtime calls
    // in togglePreview can't race the next render. A commit keeps it showing.
    const previewRef = useRef<string | null>(null);
    const endPreviewRef = useRef<(() => void) | null>(null);
    const endPreview = () => {
        const end = endPreviewRef.current;
        endPreviewRef.current = null;
        end?.();
    };
    // Leaving the panel, or moving it to another object, ends its preview, so
    // nothing stays posed with no card saying so.
    useEffect(() => {
        return () => {
            previewRef.current = null;
            setPreviewState(null);
            endPreview();
        };
    }, [behavior]);

    const togglePreview = (name: string) => {
        const next = previewState === name ? null : name;
        previewRef.current = next;

        // End the previous preview, then show the new one
        endPreview();
        if (next) endPreviewRef.current = applyPreview(behavior, next);
        setPreviewState(next);
    };

    // The panel's current object, so an action that lands after the panel moved
    // to another object leaves that object's cards alone.
    const behaviorRef = useRef(behavior);
    behaviorRef.current = behavior;

    /** The expanded card and the preview follow a renamed state (`to`), or close with a deleted one (null). */
    const followState = (from: string, to: string | null) => {
        setExpandedState((open) => (open === from ? to : open));
        if (previewRef.current !== from) return;
        previewRef.current = to;
        setPreviewState(to);
        // A deleted preview ends: its pseudo half would otherwise stay on.
        if (to === null) endPreview();
    };
    const followIfCurrent = (from: string, to: string | null) => (done: boolean) => {
        if (done && behaviorRef.current === behavior) followState(from, to);
    };

    // Delete and rename act on the GROUP that owns the state, as the strip
    // does: the owner and every member that carries it, with the Cycle/Toggle
    // rings and interaction references healed, in one undo entry. A state only
    // this object carries is changed on this object the same way. When the
    // owner is the strip's context, the edit goes through the strip's states
    // session, so the state the strip shows follows a rename or delete and page
    // looks are kept. A pseudo or compound state changes only this object.
    const removeState = (name: string) => {
        if (isObjectLocal(name)) {
            const next = { ...states };
            delete next[name];
            onChange(next);
            followState(name, null);
            return;
        }
        const owner = stateHome(behavior.sceneObject, name);
        const session = stripSessionFor(owner);
        const done = session ? session.remove(name).then((r) => r === true) : deleteStateForSubtree(owner, name);
        void done.then(followIfCurrent(name, null));
    };

    const renameState = (oldName: string, newName: string) => {
        if (oldName === 'default') return; // never rename default
        if (!newName || newName === oldName || states[newName]) return;
        if (isObjectLocal(oldName)) {
            onChange(Object.fromEntries(Object.entries(states).map(([k, v]) => [k === oldName ? newName : k, v])));
            followState(oldName, newName);
            return;
        }
        const owner = stateHome(behavior.sceneObject, oldName);
        const session = stripSessionFor(owner);
        const done = session
            ? session.rename(oldName, newName).then((r) => r === true)
            : renameStateForSubtree(owner, oldName, newName);
        void done.then(followIfCurrent(oldName, newName));
    };

    const updateState = (name: string, state: StateDefinition) => {
        onChange({ ...states, [name]: state });
    };

    // Filter out malformed state entries (AI tools can produce garbled data)
    const entries = Object.entries(states).filter(
        ([, s]) => s != null && typeof s === 'object' && 'values' in s,
    );

    return (
        <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                States
            </div>
            {entries.length === 0 && (
                <div style={{ color: '#555', fontSize: '11px', fontStyle: 'italic', marginBottom: '6px' }}>
                    No states defined
                </div>
            )}
            {entries.map(([name, state]) => {
                const isExpanded = expandedState === name;
                const isPseudo = name.startsWith(':');
                const isPreview = previewState === name;
                const isInitial = initialState === name;
                return (
                    <div key={name} style={{
                        marginBottom: '4px', backgroundColor: '#1e1e1e', borderRadius: '4px',
                        border: isPreview ? '1px solid #5a8' : '1px solid #333',
                    }}>
                        {/* Header */}
                        <div
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', cursor: 'pointer' }}
                            onClick={() => setExpandedState(isExpanded ? null : name)}
                        >
                            <span style={{ fontSize: '8px', color: '#666', width: '10px' }}>{isExpanded ? '▼' : '▶'}</span>
                            {isPseudo ? (
                                <span style={{ fontSize: '11px', color: '#8af', fontFamily: 'monospace', flex: 1 }}>{name}</span>
                            ) : (
                                <StateNameInput
                                    name={name}
                                    allNames={Object.keys(states)}
                                    onRename={(newName) => renameState(name, newName)}
                                />
                            )}
                            {onInitialStateChange && !name.includes(':') && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onInitialStateChange(isInitial ? undefined : name);
                                    }}
                                    style={{
                                        fontSize: '8px', border: `1px solid ${isInitial ? '#5a8' : '#444'}`,
                                        borderRadius: '2px', padding: '0 3px', background: 'none',
                                        color: isInitial ? '#5a8' : '#555', cursor: 'pointer',
                                    }}
                                    title={isInitial ? 'Clear initial state' : 'Set as initial state'}
                                >
                                    initial
                                </button>
                            )}
                            <span style={{ fontSize: '9px', color: '#555' }}>
                                {Object.keys(state.values).length}v
                                {state.transform ? ' +t' : ''}
                            </span>
                            {(
                                <button
                                    onClick={(e) => { e.stopPropagation(); togglePreview(name); }}
                                    style={{ background: 'none', border: 'none', color: isPreview ? '#5a8' : '#666', cursor: 'pointer', padding: '0 3px', fontSize: '10px' }}
                                    title={isPreview ? 'Stop preview' : 'Preview state'}
                                >{isPreview ? '■' : '▶'}</button>
                            )}
                            {/* Add modifier (compound state) -- only for custom states without existing pseudo */}
                            {!isPseudo && !name.includes(':') && (
                                <select
                                    value=""
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        if (!e.target.value) return;
                                        const compoundName = `${name}${e.target.value}`;
                                        if (!states[compoundName]) {
                                            // Duplicate current state's values as starting point
                                            onChange({
                                                ...states,
                                                [compoundName]: {
                                                    values: { ...state.values },
                                                    transition: defaultStateTransition(),
                                                },
                                            });
                                            setExpandedState(compoundName);
                                        }
                                        e.target.value = '';
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#8af', cursor: 'pointer', fontSize: '10px', padding: '0', width: '20px', fontFamily: 'monospace' }}
                                    title="Add pseudo-state modifier"
                                >
                                    <option value="">+:</option>
                                    {PSEUDO_STATES.filter(p => !states[`${name}${p}`]).map(p => (
                                        <option key={p} value={p}>{name}{p}</option>
                                    ))}
                                </select>
                            )}
                            {(
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeState(name); }}
                                    style={{ background: 'none', border: 'none', color: '#a55', cursor: 'pointer', padding: '0 3px', fontSize: '12px' }}
                                    title="Remove state"
                                >x</button>
                            )}
                        </div>
                        {/* Expanded content */}
                        {isExpanded && (
                            <StateDetailEditor
                                state={state}
                                stateName={name}
                                propertyNames={propertyNames}
                                propertyDefs={propertyDefs}
                                onChange={(s) => updateState(name, s)}
                            />
                        )}
                    </div>
                );
            })}
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                <button
                    onClick={addState}
                    style={{
                        flex: 1, padding: '6px', backgroundColor: '#383838',
                        border: '1px dashed #555', borderRadius: '4px', color: '#aaa',
                        cursor: 'pointer', fontSize: '11px',
                    }}
                >+ State</button>
                {/* Pseudo-state buttons -- only show unregistered ones */}
                {PSEUDO_STATES.filter(p => !states[p]).map(pseudo => (
                    <button
                        key={pseudo}
                        onClick={() => addPseudoState(pseudo)}
                        style={{
                            padding: '6px 8px', backgroundColor: '#2a2a3a',
                            border: '1px dashed #558', borderRadius: '4px', color: '#8af',
                            cursor: 'pointer', fontSize: '10px', fontFamily: 'monospace',
                        }}
                        title={`Add ${pseudo} pseudo-state`}
                    >{pseudo}</button>
                ))}
            </div>

            {/* Initial state selector */}
            {onInitialStateChange && entries.filter(([n]) => n !== 'default').length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                    <label style={{ fontSize: '11px', color: '#777' }}>Initial</label>
                    <select
                        value={initialState ?? ''}
                        onChange={(e) => onInitialStateChange(e.target.value || undefined)}
                        style={{ ...styles.input, flex: 1, fontSize: '10px', padding: '2px 4px', cursor: 'pointer' }}
                    >
                        <option value="">None</option>
                        {entries.filter(([n]) => !n.startsWith(':') && n !== 'default').map(([n]) => <option key={n} value={n}>{n}</option>)}
                    </select>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Properties Editor (main)
// ============================================================================

// ============================================================================
// Sheet Import/Export
// ============================================================================

/** Sheet format — portable properties + states */
interface PropertySheet {
    properties: PropertyDeclaration[];
    states?: Record<string, StateDefinition>;
}

/** Download declarations + states as a portable JSON sheet */
function downloadSheet(declarations: PropertyDeclaration[], states: Record<string, StateDefinition>): void {
    const sheet: PropertySheet = { properties: declarations };
    const stateEntries = Object.entries(states);
    if (stateEntries.length > 0) {
        sheet.states = states;
    }
    const blob = new Blob([JSON.stringify(sheet, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'property-sheet.json';
    a.click();
    URL.revokeObjectURL(url);
}

/** Validate imported JSON as a PropertySheet */
function validateSheet(data: unknown): PropertySheet | null {
    if (typeof data !== 'object' || data === null) return null;
    const record = data as Record<string, unknown>;

    // Must have properties array
    if (!Array.isArray(record.properties)) return null;
    for (const item of record.properties) {
        if (typeof item !== 'object' || item === null) return null;
        const prop = item as Record<string, unknown>;
        if (typeof prop.name !== 'string' || typeof prop.type !== 'string') return null;
        if (prop.value === undefined) return null;
    }

    // States are optional
    if (record.states !== undefined && (typeof record.states !== 'object' || record.states === null)) {
        return null;
    }

    return {
        properties: record.properties as PropertyDeclaration[],
        states: record.states as Record<string, StateDefinition> | undefined,
    };
}

const SheetControls: React.FC<{
    declarations: PropertyDeclaration[];
    states: Record<string, StateDefinition>;
    onChange: (decls: PropertyDeclaration[], states: Record<string, StateDefinition>) => void;
}> = ({ declarations, states, onChange }) => {
    const importRef = useRef<HTMLInputElement>(null);
    const hasContent = declarations.length > 0 || Object.keys(states).length > 0;

    return (
        <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
            <button
                onClick={() => downloadSheet(declarations, states)}
                disabled={!hasContent}
                style={{
                    flex: 1, padding: '4px 8px', backgroundColor: '#383838',
                    border: '1px solid #555', borderRadius: '4px',
                    color: hasContent ? '#aaa' : '#555',
                    cursor: hasContent ? 'pointer' : 'default',
                    fontSize: '11px',
                }}
                title="Export declarations + states as a portable JSON sheet"
            >
                Export sheet
            </button>
            <button
                onClick={() => importRef.current?.click()}
                style={{
                    flex: 1, padding: '4px 8px', backgroundColor: '#383838',
                    border: '1px solid #555', borderRadius: '4px', color: '#aaa',
                    cursor: 'pointer', fontSize: '11px',
                }}
                title="Import declarations + states from a JSON sheet (merges with existing)"
            >
                Import sheet
            </button>
            <input
                ref={importRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const parsed = JSON.parse(reader.result as string) as unknown;
                            const sheet = validateSheet(parsed);
                            if (!sheet) {
                                console.warn('[Properties] Invalid sheet — expected { properties: [...], states?: {...} }');
                                return;
                            }
                            // Merge declarations: imported override existing at same name
                            const mergedDecls = new Map<string, PropertyDeclaration>();
                            for (const d of declarations) mergedDecls.set(d.name, d);
                            for (const d of sheet.properties) mergedDecls.set(d.name, d);
                            // Merge states: imported override existing at same state name
                            const mergedStates = { ...states, ...(sheet.states ?? {}) };
                            onChange(Array.from(mergedDecls.values()), mergedStates);
                        } catch {
                            console.warn('[Properties] Failed to parse JSON sheet');
                        }
                    };
                    reader.readAsText(file);
                    e.target.value = '';
                }}
            />
        </div>
    );
};

/**
 * Write `url` into the style slot (index 0), preserving everything layered
 * after it, and normalize the result the way `clearStyleSheet` does: trailing
 * empty slots are dropped, and an emptied list becomes `undefined` rather than
 * `[]` — an empty array reads as "styled with nothing".
 */
function replaceStyleSlot(sheets: string[], url: string): string[] | undefined {
    const next = [...sheets];
    next[0] = url;
    while (next.length > 0 && !next[next.length - 1]) next.pop();
    return next.length > 0 ? next : undefined;
}

export interface PropertiesEditorProps {
    behavior: Properties;
    declarations: PropertyDeclaration[];
    sheets?: string[];
    states: Record<string, StateDefinition>;
    onChange: (decls: PropertyDeclaration[], states: Record<string, StateDefinition>) => void;
    onSheetsChange?: (sheets: string[] | undefined) => void;
    onStatesChange: (states: Record<string, StateDefinition>) => void;
    initialState?: string;
    onInitialStateChange?: (name: string | undefined) => void;
}

export const PropertiesEditor: React.FC<PropertiesEditorProps> = ({ behavior, declarations, sheets, states, onChange, onSheetsChange, onStatesChange, initialState, onInitialStateChange }) => {
    // Note: external value changes (e.g. AutoUI commit) are handled by
    // BehaviorSection's onValueChanged hook, which refreshes localData
    // without re-committing.

    // Collect ancestor-only properties (for override detection in DeclarationsEditor)
    const ancestorPropertyDefs = new Map<string, PropertyDeclaration>();
    let ancestor = behavior.sceneObject.getParent();
    while (ancestor) {
        const ancestorProps = ancestor.getBehavior('Properties');
        if (ancestorProps) {
            for (const p of ancestorProps.getProperties()) {
                if (!ancestorPropertyDefs.has(p.name)) {
                    ancestorPropertyDefs.set(p.name, p);
                }
            }
        }
        ancestor = ancestor.getParent();
    }

    // All available properties: local + ancestor (for states editor)
    const allPropertyDefs = new Map<string, PropertyDeclaration>(
        behavior.getProperties().map(p => [p.name, p]),
    );
    for (const [name, decl] of ancestorPropertyDefs) {
        if (!allPropertyDefs.has(name)) {
            allPropertyDefs.set(name, decl);
        }
    }
    const allPropertyNames = Array.from(allPropertyDefs.keys());

    // The `declarations` we render are already seeded with the active keyframe's
    // stored values (the panel overlays them via overlayKeyframeValues when it
    // builds localData — see BehaviorSection). So the value fields are driven by
    // this single, synchronous source: typing updates `decl.value` immediately
    // and a keyframe switch reseeds it. We intentionally do NOT layer a separate
    // per-render resolver on top — that raced the async keyframe write and
    // masked in-progress typing with the stale stored value.

    return (
        <div>
            <DeclarationsEditor
                declarations={declarations}
                sheetDeclarations={behavior.getSheetDeclarations()}
                ancestorProperties={ancestorPropertyDefs}
                states={states}
                onChange={onChange}
                onBindToRenderer={(propertyName, binding) => {
                    // Shared helper commits the `$name` patch, records undo, and
                    // dispatches the behavior-changed event.
                    // The ternary looks redundant but narrows `binding` so `{ binding }`
                    // matches one RendererBindingTarget union member; the unnarrowed
                    // 3-literal union is not assignable to either member.
                    void bindRendererProperty(
                        behavior.sceneObject,
                        propertyName,
                        binding === 'opacity' ? { binding } : { binding },
                    ).then(({ errors }) => {
                        if (errors.length > 0) {
                            console.warn('[Properties] Renderer binding failed:', errors);
                        }
                    });
                }}
                getMaterialSrcSeed={() => {
                    const src = behavior.sceneObject.getBehavior(Renderer)?.getData()
                        .materials?.find(m => (m.targetMaterialIndex ?? 0) === 0)?.src;
                    // An already-bound src ("$other") is not a seedable URL
                    return src && !isPropertyBinding(src) ? src : undefined;
                }}
            />


            <StatesEditor
                behavior={behavior}
                states={states}
                propertyNames={allPropertyNames}
                propertyDefs={allPropertyDefs}
                onChange={onStatesChange}
                initialState={initialState}
                onInitialStateChange={onInitialStateChange}
            />

            {/* Sheet src + import/export */}
            <div style={{ marginTop: '12px' }}>
                {onSheetsChange && (
                    <>
                        <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                            Style sheet
                        </div>
                        {sheets && sheets.length > 0 ? (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '6px' }}>
                                <input
                                    type="text"
                                    value={sheets[0]}
                                    // Slot 0 is the style slot; entries after it are layered
                                    // declaration sheets. Replace within the slot — writing
                                    // `[value]` here truncated the array and silently dropped
                                    // every sheet the user had layered underneath.
                                    onChange={(e) => onSheetsChange(replaceStyleSlot(sheets, e.target.value))}
                                    placeholder="./assets/theme.json"
                                    style={{ ...styles.input, flex: 1, fontSize: '11px', backgroundColor: '#1a1a2e' }}
                                />
                                <button
                                    onClick={() => onSheetsChange(replaceStyleSlot(sheets, ''))}
                                    style={{ background: 'none', border: 'none', color: '#a55', cursor: 'pointer', fontSize: '12px', padding: '0 3px' }}
                                    title="Remove style sheet reference"
                                >×</button>
                            </div>
                        ) : (
                            <button
                                onClick={() => onSheetsChange([''])}
                                style={{
                                    width: '100%', padding: '4px', backgroundColor: '#383838',
                                    border: '1px dashed #555', borderRadius: '4px', color: '#aaa',
                                    cursor: 'pointer', fontSize: '11px', marginBottom: '6px',
                                }}
                            >+ Sheet src</button>
                        )}
                    </>
                )}
                <SheetControls declarations={declarations} states={states} onChange={onChange} />
            </div>
        </div>
    );
};
